/**
 * MutationSystem — evolution tree economy.
 *
 * The player permanently picks mutations into owned slots, then ranks them
 * up. Picks and ranks both spend run-only DNA and cannot be undone for the
 * rest of the run.
 *
 *   pickCost(id)    — DNA to first-pick a mutation (only if a slot is open)
 *   rankCost(id)    — DNA to advance an owned mutation by 1 rank
 *   slotCost()      — DNA to unlock the next slot (if any)
 *
 * Pricing curves:
 *   - Rank cost: pickCost × 1.7^(rank-1). The pickCost itself is rank-1.
 *   - Slot cost: SLOT_COSTS[slotsOwned] (slot 1 is free at run start).
 *
 * getEffects() resolves the EffectSet by summing each owned mutation's
 * effectPerRank × rank, plus family synergies (when 2+ different mutations
 * of one family are owned), plus environmental photosynthesis scaling.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import type { EffectSet, MutationDef, MutationFamily } from '@/data/types';
import { player } from './PlayerState';
import type { EnvironmentSystem } from './EnvironmentSystem';

export const MAX_SLOTS = 4;
export const DEFAULT_MAX_RANK = 5;
export const RANK_COST_SCALE = 1.7;
/** Index = next slot to buy (1-based). slotsOwned=1 means buying slot 2 costs SLOT_COSTS[1]. */
export const SLOT_COSTS = [0, 30, 100, 250];

export class MutationSystem {
  private cachedEffects: EffectSet = {};
  private dirty = true;
  private envRef: EnvironmentSystem | null = null;

  constructor(
    private defs: Record<string, MutationDef>,
    private families: Record<string, MutationFamily>,
  ) {}

  setEnvironment(env: EnvironmentSystem): void {
    this.envRef = env;
  }

  allDefs(): MutationDef[] { return Object.values(this.defs); }
  allFamilies(): MutationFamily[] { return Object.values(this.families); }
  getDef(id: string): MutationDef | undefined { return this.defs[id]; }
  getFamily(id: string): MutationFamily | undefined { return this.families[id]; }

  // --- run state accessors ---
  ranks(): Record<string, number> { return saveManager.getRun().mutationRanks; }
  rankOf(id: string): number { return saveManager.getRun().mutationRanks[id] ?? 0; }
  isOwned(id: string): boolean { return this.rankOf(id) > 0; }
  /** Mutation IDs the player has picked at least once this run. */
  ownedIds(): string[] {
    return Object.entries(saveManager.getRun().mutationRanks)
      .filter(([, r]) => r > 0)
      .map(([id]) => id);
  }
  slotsOwned(): number { return saveManager.getRun().slotsOwned; }
  slotsFilled(): number { return this.ownedIds().length; }
  hasOpenSlot(): boolean { return this.slotsFilled() < this.slotsOwned(); }

  // --- pricing ---
  maxRank(id: string): number { return this.defs[id]?.maxRank ?? DEFAULT_MAX_RANK; }

  /** Cost in DNA to advance `id` by one rank (or first-pick if rank=0). */
  rankCost(id: string): number {
    const def = this.defs[id]; if (!def) return Infinity;
    const r = this.rankOf(id);
    if (r >= this.maxRank(id)) return Infinity;
    return Math.round(def.pickCost * Math.pow(RANK_COST_SCALE, r));
  }

  /** Cost in DNA to unlock the next slot, or Infinity if all owned. */
  slotCost(): number {
    const owned = this.slotsOwned();
    if (owned >= MAX_SLOTS) return Infinity;
    return SLOT_COSTS[owned] ?? Infinity;
  }

  // --- transactions ---
  /** Pick or rank up a mutation. Returns true on success. */
  buyRank(id: string): boolean {
    const def = this.defs[id]; if (!def) return false;
    const r = this.rankOf(id);
    if (r >= this.maxRank(id)) return false;
    if (r === 0 && !this.hasOpenSlot()) return false;
    const cost = this.rankCost(id);
    if (!player.spendDna(cost)) return false;
    const run = saveManager.getRun();
    run.mutationRanks[id] = r + 1;
    this.dirty = true;
    if (r === 0) {
      bus.emit('mutation:picked', { mutationId: id });
      if (def.codexId) bus.emit('codex:unlocked', { entryId: def.codexId });
    } else {
      bus.emit('mutation:ranked', { mutationId: id, rank: r + 1 });
    }
    this.updateSynergyEvents();
    return true;
  }

  /** Buy the next mutation slot. Returns true on success. */
  buySlot(): boolean {
    const owned = this.slotsOwned();
    if (owned >= MAX_SLOTS) return false;
    const cost = this.slotCost();
    if (!player.spendDna(cost)) return false;
    saveManager.getRun().slotsOwned = owned + 1;
    bus.emit('mutation:slotUnlocked', { slot: owned + 1 });
    return true;
  }

  // --- effect resolution ---
  getEffects(): EffectSet {
    if (this.dirty) this.rebuild();
    return this.cachedEffects;
  }

  markDirty(): void { this.dirty = true; }

  private rebuild(): void {
    const out: EffectSet = {};
    const familyCounts: Record<string, number> = {};

    for (const id of this.ownedIds()) {
      const def = this.defs[id]; if (!def) continue;
      const r = this.rankOf(id);
      if (r <= 0) continue;
      familyCounts[def.family] = (familyCounts[def.family] ?? 0) + 1;
      addScaled(out, def.effectPerRank, r);
    }

    for (const [fid, count] of Object.entries(familyCounts)) {
      if (count >= 2) {
        const fam = this.families[fid];
        if (fam) addScaled(out, fam.synergy, 1);
      }
    }

    // environment scaling for photosynthesis-family effects
    const env = this.envRef?.getEffects();
    if (env && env.photosynthesisMult) {
      const m = env.photosynthesisMult;
      if (out.passiveBiomassPerSec) out.passiveBiomassPerSec *= m;
      if (out.passiveBiomassStationary) out.passiveBiomassStationary *= m;
      if (out.atpRegenStationaryBonus) out.atpRegenStationaryBonus *= m;
    }

    this.cachedEffects = out;
    this.dirty = false;
  }

  private updateSynergyEvents(): void {
    const counts: Record<string, number> = {};
    for (const id of this.ownedIds()) {
      const def = this.defs[id]; if (!def) continue;
      counts[def.family] = (counts[def.family] ?? 0) + 1;
    }
    for (const fam of this.allFamilies()) {
      bus.emit('mutation:synergy', {
        family: fam.id,
        active: (counts[fam.id] ?? 0) >= 2,
      });
    }
  }
}

function addScaled(target: EffectSet, src: EffectSet, scale: number): void {
  for (const key of Object.keys(src) as (keyof EffectSet)[]) {
    const v = src[key];
    if (v === undefined) continue;
    target[key] = ((target[key] as number | undefined) ?? 0) + (v as number) * scale;
  }
}
